import React, { useState, useEffect, useRef } from 'react';
import '../styles/reset.css';
import '@sumup-oss/design-tokens/light.css';
import '@sumup-oss/design-tokens/fonts.css';
import '@sumup-oss/circuit-ui/styles.css';
import { AppProps as NextAppProps } from 'next/app';
import { NextPage } from 'next';
import { appWithTranslation } from 'next-i18next';
import { Entry } from 'contentful';
import { ThemeProvider } from '@emotion/react';
import { ModalProvider } from '@sumup-oss/circuit-ui';
import { light } from '@sumup-oss/design-tokens';
import { elb } from '@elbwalker/walker.js';
import { clarity } from 'react-microsoft-clarity';
import { Col, Row } from '@sumup-oss/circuit-ui/legacy';
import { SpeedInsights } from '@vercel/speed-insights/next';

import * as ANALYTICS_CONSTANTS from 'constants/analytics';
import { COOKIES } from 'constants/common';
import { DEFAULT_LOCALES } from 'constants/localization';
import { CookieConsentScript } from 'components/CookieConsent/public';
import GeolocationModal from 'components/GeolocationModal';
import Footer from 'components/Footer';
import { FooterData } from 'components/Footer/FooterTypes';
import Header from 'components/Header';
import { Meta } from 'components/Meta';
import PreviewButton from 'components/PreviewButton';
import TopNav from 'components/TopNav';
import { Overlay, HeaderSmallGrid } from 'components/styledComponents';
import { CustomLiveAgentProvider } from 'providers/CustomLiveAgent';
import { LiveAgentProvider } from 'providers/LiveAgent';
import OptimizelyProvider from 'providers/Optimizely';
import { SearchResultsProvider } from 'providers/SearchResults';
import { ScrollProvider } from 'providers/SearchScroll';
import * as Analytics from 'services/analytics';
import { GtmScript } from 'services/gtm';
import { getCountryDataForLocale } from 'services/localization';
import { getCurrentPageName } from 'services/pages/common';
import { setCookie } from 'services/storage';
import { ContentfulCountry, MetaData } from 'types/common';
import 'types/global';
import {
  getOptimizelyAnonymousUser,
  optimizelyClient,
} from 'src/services/optimizely/client';
import Chat from 'src/components/Chat';
import ClientOnly from 'src/utils/client-only';
import ChatProvider from 'src/providers/Chat/Chat';

type AppProps<P> = {
  pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

type PageProps = {
  meta?: MetaData;
  articleId?: string;
  sectionId?: string;
  footerData?: FooterData;
  countriesData?: Entry<ContentfulCountry>[];
  preview?: null | true;
};

const App: NextPage<AppProps<PageProps>> = ({
  Component,
  pageProps,
  router,
}) => {
  const [overlayEnabled, setOverlayEnabled] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const currentPage = getCurrentPageName(router);

  const [isChatEnabled, setIsChatEnabled] = useState(false);
  useEffect(() => {
    const country = router.locale.split('-')[1];
    const user = getOptimizelyAnonymousUser(country);
    optimizelyClient
      .onReady()
      .then(() => {
        const isChatFeatureEnabled = optimizelyClient.isFeatureEnabled(
          '_support_centre__chat',
          user.id,
          user.attributes,
        );
        setIsChatEnabled(isChatFeatureEnabled);
      })
      .catch(() => {
        setIsChatEnabled(false);
      });
  }, [router.locale]);

  useEffect(() => {
    if (router.asPath) {
      elb('walker run');
      Analytics.AnalyticsEvents.sendEvent({
        'content-name': router.asPath,
        'event': ANALYTICS_CONSTANTS.EVENTS.CONTENT_VIEW,
        'pageType': Analytics.AnalyticsEvents.getPageType(currentPage),
        'locale': router.locale || DEFAULT_LOCALES.APP,
        ...(pageProps?.articleId && {
          article_id: pageProps.articleId,
        }),
      });
      Analytics.AnalyticsEvents.sendPageView({
        pageType: Analytics.AnalyticsEvents.getPageType(currentPage),
        locale: router.locale || DEFAULT_LOCALES.APP,
        ...(pageProps?.articleId && {
          article_id: pageProps.articleId,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (overlayEnabled) {
      overlayRef.current.addEventListener(
        'touchstart',
        (e) => e.preventDefault(),
        {
          passive: false,
        },
      );
    }
  }, [overlayEnabled]);

  useEffect(() => {
    setCookie({
      name: COOKIES.APP_LOCALE,
      value: router.locale,
      exdays: 1,
    });
  });

  useEffect(() => {
    clarity.init('lyy7nqnneg');
    clarity.consent();
    clarity.setTag('locale', router.locale);
  });

  return (
    <ThemeProvider theme={light}>
      <OptimizelyProvider>
        <ModalProvider>
          <CustomLiveAgentProvider>
            <LiveAgentProvider
              locale={router.locale}
              shouldLoad={Object.keys(pageProps).length > 0}
              countryData={getCountryDataForLocale({
                locale: router.locale,
                countriesData: pageProps.countriesData,
              })}
              articleId={pageProps.articleId}
            >
              <ScrollProvider>
                <SearchResultsProvider>
                  <PreviewButton previewEnabled={pageProps.preview} />
                  <Meta {...pageProps.meta} />
                  <SpeedInsights sampleRate={0.1} />
                  {/* OneTrust cookie consent */}
                  {process.env.NEXT_PUBLIC_ONETRUST_ENABLED && (
                    <CookieConsentScript />
                  )}
                  <GtmScript />
                  <GeolocationModal
                    articleId={pageProps?.articleId}
                    sectionId={pageProps?.sectionId}
                  />
                  {/* Unified Chat Start */}
                  {isChatEnabled && (
                    <ClientOnly>
                      <ChatProvider>
                        <Chat locale={router.locale} />
                      </ChatProvider>
                    </ClientOnly>
                  )}
                  {/* Unified Chat End */}
                  {overlayEnabled ? (
                    <Overlay
                      ref={overlayRef}
                      onClick={() => setOverlayEnabled(false)}
                      onTouchEnd={() => setOverlayEnabled(false)}
                      enabled={overlayEnabled}
                    />
                  ) : null}
                  {Object.keys(pageProps).length > 0 && (
                    <>
                      <TopNav
                        overlayEnabled={overlayEnabled}
                        setOverlayEnabled={setOverlayEnabled}
                        countriesData={pageProps.countriesData}
                      />
                      <HeaderSmallGrid>
                        {currentPage === 'ai-search' ? (
                          <Component {...pageProps} key={router.asPath} />
                        ) : (
                          <>
                            <Header
                              overlayEnabled={overlayEnabled}
                              setOverlayEnabled={setOverlayEnabled}
                            />
                            <Row>
                              <Col span="12" skip="0">
                                <Component {...pageProps} key={router.asPath} />
                              </Col>
                            </Row>
                          </>
                        )}
                      </HeaderSmallGrid>
                      <Footer
                        locale={router.locale}
                        footerData={pageProps.footerData}
                        countriesData={pageProps.countriesData}
                      />
                    </>
                  )}
                </SearchResultsProvider>
              </ScrollProvider>
            </LiveAgentProvider>
          </CustomLiveAgentProvider>
        </ModalProvider>
      </OptimizelyProvider>
    </ThemeProvider>
  );
};
export default appWithTranslation(App);
